import { Commons } from '../commons/commons';
import { LogLevel, LogLevelTypes } from '../types/log.types';

export interface LogRequest {
    message?: string;
    data?: any;
    error?: Error;
}

export interface LogServiceProvider {
    log: (logEntry: LogEntry) => void;
    getBrowserInfo: () => any,
}

export interface LogEntry {
    message: string;
    level: string;
    loggerName?: string;
    data?: any;
    error?: Error;
    errorName?: string;
    errorMessage?: string;
    errorStack?: string;
    errorCategory?: string;
    sessionDetails?: string;
    browserDetails?: string;
    url?: string;
    wsId: string;
    date: Date;
    /**
     * The `Accept` header the failing HTTP request carried (`<none>` when no
     * explicit Accept was set), captured by LightHttp on failed requests and
     * threaded here so client-side error reports help diagnose upstream
     * `406 Could not parse Accept header` failures. Only present when `err`
     * is one of LightHttp's wrapped HTTP errors — additive, never set
     * otherwise.
     */
    requestAcceptHeader?: string;
}

/**
 * Narrow shape for reading the optional `requestAcceptHeader` property off an
 * arbitrary Error without importing utils/request.utils.ts here — that file
 * imports Logger, so a runtime import back would create a cycle (see
 * reference_fe_circular_import_tdz: jest tolerates cycles but the webpack SSG
 * build TDZ-crashes on them).
 */
interface ErrorWithRequestAcceptHeader extends Error {
    requestAcceptHeader?: string;
}

export class Logger {
    private static loggerService: LogServiceProvider;
    private static sessionDetails: any;
    private static wsId;
    private static cachedBrowserInfo: any = undefined;
    private static pendingLogs: LogEntry[] = [];

    private static getCachedBrowserInfo(): any {
        if (Logger.cachedBrowserInfo === undefined && Logger.loggerService) {
            Logger.cachedBrowserInfo = Logger.loggerService.getBrowserInfo();
        }
        return Logger.cachedBrowserInfo;
    }

    public static getLogger(name: string): Logger {
        return new Logger(name);
    }

    constructor(
        public name: string,
    ) { }

    public static setLoggerService(loggerService: LogServiceProvider) {
        return Logger.loggerService = loggerService;
    }

    public static setWSId(wsId: any) {
        return Logger.wsId = wsId;
    }

    public static setSessionDetails(sessionDetails: any) {
        return Logger.sessionDetails = sessionDetails;
    }

    private _log(message: string, level: LogLevel, data?: any, err?: Error) {
        // Skip logging for bots
        const browserInfo = Logger.getCachedBrowserInfo();
        if (browserInfo?.type === 'bot-device' || browserInfo?.bot) return;

        if (Commons.DEBUG) {
            if (message) {
                console.log(message);
            }
            if (err) {
                console.error(err);
            }
            if (data) {
                console.log(data);
            }
        }

        const requestAcceptHeader = (err as ErrorWithRequestAcceptHeader)?.requestAcceptHeader;
        const logEntry: LogEntry = Object.assign(
            {},
            this.getBaseObject(),
            {
                message,
                errorName: err ? err.name : null,
                errorMessage: err ? err.message : null,
                errorStack: err ? err.stack : null,
                loggerName: this.name,
                level: LogLevelTypes.logLevelToReadableString(level),
                sessionDetails: Logger.sessionDetails,
                browserDetails: browserInfo || null,
                url: typeof window !== 'undefined' ? window.location?.href : undefined,
                userTimestamp: `${(new Date()).toISOString()}`,
            },
            (data !== undefined && data !== null ? { data } : {}),
            (requestAcceptHeader !== undefined ? { requestAcceptHeader } : {}),
        );

        if (Logger.loggerService) {
            Logger.flushPendingLogs();
            Logger.loggerService.log(logEntry);
        } else {
            Logger.pendingLogs.push(logEntry);
            if (Commons.DEBUG) {
                console.log('LogService not ready, buffered:', message);
            }
        }
    }

    private static flushPendingLogs() {
        if (Logger.pendingLogs.length === 0) return;
        const pending = Logger.pendingLogs;
        Logger.pendingLogs = [];
        pending.forEach(entry => {
            if (!entry.browserDetails) entry.browserDetails = Logger.getCachedBrowserInfo();
            Logger.loggerService.log(entry);
        });
    }

    private log(level: LogLevel, request: LogRequest | Error | string | String) {
        if (request == null) {
            this._log('No message provided', level, null);
        } else if (typeof request === 'string' || request instanceof String) {
            this._log(request.toString(), level, null);
        } else if (request instanceof Error) {
            this._log(request.message, level, null, request);
        } else {
            this._log(request.message, level, request.data, request.error);
        }
    }

    public trace1(request: LogRequest | string) {
        if (request && typeof request === 'object' && !(request instanceof Error) && !(request instanceof String)) {
            (request as LogRequest).message = (request as LogRequest).message || 'No message provided';
        }
        this.log(LogLevelTypes.TRACE_1, request);
    }

    public trace7(request: LogRequest | string | String) {
        if (request && typeof request === 'object' && !(request instanceof Error) && !(request instanceof String)) {
            (request as LogRequest).message = (request as LogRequest).message || 'No message provided';
        }
        this.log(LogLevelTypes.TRACE_7, request);
    }

    public debug(request: LogRequest | string | String) {
        if (request && typeof request === 'object' && !(request instanceof Error) && !(request instanceof String)) {
            (request as LogRequest).message = (request as LogRequest).message || 'No message provided';
        }
        this.log(LogLevelTypes.DEBUG, request);
    }

    public info(request: LogRequest | string | String) {
        if (request && typeof request === 'object' && !(request instanceof Error) && !(request instanceof String)) {
            (request as LogRequest).message = (request as LogRequest).message || 'No message provided';
        }
        this.log(LogLevelTypes.INFO, request);
    }

    public warn(request: LogRequest | Error | string | String) {
        if (request && typeof request === 'object' && !(request instanceof Error) && !(request instanceof String)) {
            (request as LogRequest).message = (request as LogRequest).message || 'No message provided';
        }
        this.log(LogLevelTypes.WARN, request);
    }

    public error(request: LogRequest | Error | string | String) {
        if (request && typeof request === 'object' && !(request instanceof Error) && !(request instanceof String)) {
            (request as LogRequest).message = (request as LogRequest).message || 'No message provided';
        }
        this.log(LogLevelTypes.ERROR, request);
    }

    public fatal(request: LogRequest | Error | string | String) {
        if (request && typeof request === 'object' && !(request instanceof Error) && !(request instanceof String)) {
            (request as LogRequest).message = (request as LogRequest).message || 'No message provided';
        }
        this.log(LogLevelTypes.FATAL, request);
    }

    private getBaseObject() {
        return {
            wsId: Logger.wsId,
            date: new Date(),
        };
    }
}
